import React, { useCallback, useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  CircularProgress,
  Box,
  IconButton,
  Card,
  CardContent,
  Chip,
  useTheme,
  Autocomplete,
  Avatar,
  ListItem,
  ListItemAvatar,
  ListItemText,
} from '@mui/material';
import { Photo, Close, LocationOn, Link as LinkIcon, Business as BusinessIcon } from '@mui/icons-material';
import toast from 'react-hot-toast';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Descendant, Text as SlateText, Element as SlateElement } from 'slate';
import {
  ConferenceEvent,
  CreateEventRequest,
  UpdateEventRequest,
  EVENT_CATEGORIES,
  EventCategory,
  EventMode,
  EventVisibility,
  TicketType,
  Business,
} from '@/constants/types';
import { uploadService } from '@/services/upload/uploadService';
import { SlateRichTextEditor } from '@/components/dashboard/SlateRichTextEditor';
import { businessService } from '@/services/business/businessService';
import { getFileUrl } from '@/utils/businessHelpers';

// ── Slate serialisation helpers (mirrors CreatePostModal) ──────────────────

const serializeNode = (node: Descendant): string => {
  if (SlateText.isText(node)) {
    let text = node.text;
    if ((node as any).code) text = `<code>${text}</code>`;
    if ((node as any).bold) text = `<strong>${text}</strong>`;
    if ((node as any).italic) text = `<em>${text}</em>`;
    if ((node as any).underline) text = `<u>${text}</u>`;
    if ((node as any).strikethrough) text = `<s>${text}</s>`;
    return text;
  }
  if (SlateElement.isElement(node)) {
    const children = node.children.map((n) => serializeNode(n)).join('');
    const align = (node as any).align;
    const style = align ? ` style="text-align: ${align}"` : '';
    switch (node.type) {
      case 'block-quote': return `<blockquote${style}>${children}</blockquote>`;
      case 'heading-one': return `<h1${style}>${children}</h1>`;
      case 'heading-two': return `<h2${style}>${children}</h2>`;
      case 'heading-three': return `<h3${style}>${children}</h3>`;
      case 'heading-four': return `<h4${style}>${children}</h4>`;
      case 'heading-five': return `<h5${style}>${children}</h5>`;
      case 'heading-six': return `<h6${style}>${children}</h6>`;
      case 'bulleted-list': return `<ul>${children}</ul>`;
      case 'numbered-list': return `<ol>${children}</ol>`;
      case 'list-item': return `<li>${children}</li>`;
      case 'link': return `<a href="${(node as any).url}" target="_blank" rel="noopener noreferrer">${children}</a>`;
      case 'paragraph':
      default: return `<p${style}>${children}</p>`;
    }
  }
  return '';
};

const serializeSlateToHtml = (nodes: Descendant[]): string =>
  nodes.map(serializeNode).join('');

const getPlainText = (nodes: Descendant[]): string =>
  nodes.map((n) => {
    if (SlateText.isText(n)) return n.text;
    if (SlateElement.isElement(n) && n.children) return getPlainText(n.children);
    return '';
  }).join('');

// Convert any existing description string to a Slate-JSON string for initialValue
const toSlateJson = (desc: string): string => {
  if (!desc) return '';
  try {
    const parsed = JSON.parse(desc);
    if (Array.isArray(parsed)) return desc; // already Slate JSON
  } catch {}
  // Strip HTML tags → plain text paragraph
  const text = desc.includes('<') ? desc.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim() : desc;
  return JSON.stringify([{ type: 'paragraph', children: [{ text }] }]);
};

// ── Validation ─────────────────────────────────────────────────────────────

const validationSchema = Yup.object({
  businessId: Yup.string().required('Business is required'),
  eventName: Yup.string().max(150, 'Max 150 characters').required('Event name is required'),
  category: Yup.string().required('Category is required'),
  startDateTime: Yup.string().required('Start date & time is required'),
  endDateTime: Yup.string()
    .optional()
    .test('after-start', 'End date must be after start date', function (value) {
      const { startDateTime } = this.parent;
      if (!value || !startDateTime) return true;
      return new Date(value) > new Date(startDateTime);
    }),
  registrationDeadline: Yup.string()
    .optional()
    .test('before-start', 'Registration deadline must be before start date', function (value) {
      const { startDateTime } = this.parent;
      if (!value || !startDateTime) return true;
      return new Date(value) < new Date(startDateTime);
    }),
  eventMode: Yup.string().oneOf(['online', 'in_person']).required('Event mode is required'),
  eventLink: Yup.string()
    .url('Must be a valid URL')
    .when('eventMode', {
      is: 'online',
      then: (schema) => schema.required('Event link is required for online events'),
      otherwise: (schema) => schema.optional(),
    }),
  venueName: Yup.string()
    .max(200, 'Max 200 characters')
    .when('eventMode', {
      is: 'in_person',
      then: (schema) => schema.required('Venue name is required for in-person events'),
      otherwise: (schema) => schema.optional(),
    }),
  addressLine1: Yup.string()
    .max(200, 'Max 200 characters')
    .when('eventMode', {
      is: 'in_person',
      then: (schema) => schema.required('Address Line 1 is required'),
      otherwise: (schema) => schema.optional(),
    }),
  addressLine2: Yup.string().max(200, 'Max 200 characters').optional(),
  city: Yup.string()
    .max(100, 'Max 100 characters')
    .when('eventMode', {
      is: 'in_person',
      then: (schema) => schema.required('City is required'),
      otherwise: (schema) => schema.optional(),
    }),
  state: Yup.string()
    .max(100, 'Max 100 characters')
    .when('eventMode', {
      is: 'in_person',
      then: (schema) => schema.required('State is required'),
      otherwise: (schema) => schema.optional(),
    }),
  pincode: Yup.string()
    .matches(/^[0-9]{6}$/, 'Must be a 6-digit pincode')
    .when('eventMode', {
      is: 'in_person',
      then: (schema) => schema.required('Pincode is required'),
      otherwise: (schema) => schema.optional(),
    }),
  googleMapsLink: Yup.string()
    .url('Must be a valid URL')
    .optional(),
  description: Yup.string()
    .required('Description is required')
    .test('has-content', 'Description is required', (value) => {
      if (!value) return false;
      try {
        const nodes = JSON.parse(value) as Descendant[];
        return getPlainText(nodes).trim().length > 0;
      } catch {
        return value.trim().length > 0;
      }
    })
    .test('max-content', 'Max 250 characters', (value) => {
      if (!value) return true;
      try {
        const nodes = JSON.parse(value) as Descendant[];
        return getPlainText(nodes).length <= 250;
      } catch {
        return value.length <= 250;
      }
    }),
  eventVisibility: Yup.string().oneOf(['public', 'private']),
  certificateProvided: Yup.boolean(),
  ticketType: Yup.string().oneOf(['free', 'paid']).required('Ticket type is required'),
  price: Yup.number().when('ticketType', {
    is: 'paid',
    then: (schema) => schema.min(1, 'Price must be at least 1').required('Price is required for paid events'),
    otherwise: (schema) => schema.optional(),
  }),
  totalSeats: Yup.number().integer('Must be a whole number').min(1, 'Must be at least 1').optional(),
});

// ── Helpers ────────────────────────────────────────────────────────────────

const toDatetimeLocal = (iso?: string) => (iso ? iso.slice(0, 16) : '');

const formatDateTime = (dateStr: string) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
};

// ── Props ──────────────────────────────────────────────────────────────────

interface EventDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: CreateEventRequest | UpdateEventRequest) => void;
  event?: ConferenceEvent | null;
  isLoading?: boolean;
  defaultBusiness?: Business | null;
}

interface EventFormValues {
  businessId: string;
  eventName: string;
  category: EventCategory;
  startDateTime: string;
  endDateTime: string;
  registrationDeadline: string;
  eventMode: EventMode;
  eventLink: string;
  venueName: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  pincode: string;
  googleMapsLink: string;
  description: string;
  eventVisibility: EventVisibility;
  certificateProvided: boolean;
  ticketType: TicketType;
  price?: number;
  totalSeats?: number;
}

// ── Component ──────────────────────────────────────────────────────────────

export const EventDialog: React.FC<EventDialogProps> = ({
  open,
  onClose,
  onSubmit,
  event,
  isLoading = false,
  defaultBusiness = null,
}) => {
  const theme = useTheme();
  const isEdit = Boolean(event);

  const [bannerFile, setBannerFile] = useState<File | null>(null);
  const [bannerPreviewUrl, setBannerPreviewUrl] = useState<string>('');
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  // Key to force SlateRichTextEditor remount when dialog opens with a different event
  const [editorKey, setEditorKey] = useState(0);

  // Business selection state
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [businessesLoading, setBusinessesLoading] = useState(false);
  const [businessPage, setBusinessPage] = useState(1);
  const [hasMoreBusinesses, setHasMoreBusinesses] = useState(true);
  const [selectedBusiness, setSelectedBusiness] = useState<Business | null>(null);

  const formik = useFormik<EventFormValues>({
    initialValues: {
      businessId: '',
      eventName: '',
      category: '' as EventCategory,
      startDateTime: '',
      endDateTime: '',
      registrationDeadline: '',
      eventMode: 'online' as EventMode,
      eventLink: '',
      venueName: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      pincode: '',
      googleMapsLink: '',
      description: '',
      eventVisibility: 'public' as EventVisibility,
      certificateProvided: false,
      ticketType: 'free' as TicketType,
      price: undefined,
      totalSeats: undefined,
    },
    validationSchema,
    onSubmit: () => {
      setIsPreviewMode(true);
    },
  });

  // Load verified businesses
  const loadBusinesses = async (page: number = 1, append: boolean = false) => {
    try {
      setBusinessesLoading(true);
      const response = await businessService.getMyBusinesses({ page, limit: 10 });
      const verifiedBusinesses = response.data.businesses.filter((b) => b.isVerified);

      if (append) {
        setBusinesses((prev) => [...prev, ...verifiedBusinesses]);
      } else {
        setBusinesses(verifiedBusinesses);
      }

      setHasMoreBusinesses(response.data.pagination.currentPage < response.data.pagination.totalPages);
    } catch (err: any) {
      toast.error(err.message || 'Failed to load businesses');
    } finally {
      setBusinessesLoading(false);
    }
  };

  // Load more businesses on scroll
  const handleBusinessScroll = (event: React.SyntheticEvent) => {
    const listboxNode = event.currentTarget;
    const position = listboxNode.scrollTop + listboxNode.clientHeight;
    const bottom = listboxNode.scrollHeight;

    if (position >= bottom - 10 && hasMoreBusinesses && !businessesLoading) {
      const nextPage = businessPage + 1;
      setBusinessPage(nextPage);
      loadBusinesses(nextPage, true);
    }
  };

  const handleDescriptionChange = useCallback((value: string) => {
    formik.setFieldValue('description', value);
    // Explicitly set touched to true on change to show errors immediately
    formik.setFieldTouched('description', true, false);
  }, [formik]);

  const handleDescriptionBlur = useCallback(() => {
    formik.setFieldTouched('description', true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (open) {
      setIsPreviewMode(false);
      setEditorKey((k) => k + 1); // remount Slate editor
      setBusinessPage(1);
      loadBusinesses(1, false); // Load businesses when dialog opens

      if (event) {
        const eventBusinessId = typeof (event as any).businessId === 'object'
          ? (event as any).businessId?._id
          : (event as any).businessId;

        formik.resetForm({
          values: {
            businessId: eventBusinessId || '',
            eventName: event.eventName,
            category: event.category,
            startDateTime: toDatetimeLocal(event.startDateTime),
            endDateTime: toDatetimeLocal(event.endDateTime),
            registrationDeadline: toDatetimeLocal(event.registrationDeadline),
            eventMode: event.eventMode,
            eventLink: event.eventLink || '',
            venueName: event.venueName || '',
            addressLine1: event.fullAddress?.addressLine1 || '',
            addressLine2: event.fullAddress?.addressLine2 || '',
            city: event.fullAddress?.city || '',
            state: event.fullAddress?.state || '',
            pincode: event.fullAddress?.pincode || '',
            googleMapsLink: event.fullAddress?.googleMapsLink || '',
            description: toSlateJson(event.description),
            eventVisibility: event.eventVisibility,
            certificateProvided: event.certificateProvided,
            ticketType: event.ticketType,
            price: event.price,
            totalSeats: event.totalSeats,
          },
        });
        setBannerPreviewUrl(event.eventBanner?.url || '');
        // Find and set selected business
        const business = businesses.find((b) => b._id === eventBusinessId);
        setSelectedBusiness(business || null);
      } else {
        formik.resetForm();
        setBannerPreviewUrl('');
        if (defaultBusiness) {
          setSelectedBusiness(defaultBusiness);
          formik.setFieldValue('businessId', defaultBusiness._id);
        } else {
          setSelectedBusiness(null);
        }
      }
      setBannerFile(null);
    }
  }, [open, event, defaultBusiness]);

  // Set selected business when businesses are loaded (for edit mode)
  useEffect(() => {
    if (event && businesses.length > 0 && !selectedBusiness) {
      const eventBusinessId = typeof (event as any).businessId === 'object'
        ? (event as any).businessId?._id
        : (event as any).businessId;
      const business = businesses.find((b) => b._id === eventBusinessId);
      if (business) {
        setSelectedBusiness(business);
      }
    }
  }, [businesses, event, selectedBusiness]);

  const handleBannerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('Image must be under 10MB'); return; }
    if (bannerPreviewUrl.startsWith('blob:')) URL.revokeObjectURL(bannerPreviewUrl);
    setBannerFile(file);
    setBannerPreviewUrl(URL.createObjectURL(file));
    e.target.value = '';
  };

  const handleRemoveBanner = () => {
    if (bannerPreviewUrl.startsWith('blob:')) URL.revokeObjectURL(bannerPreviewUrl);
    setBannerFile(null);
    setBannerPreviewUrl('');
  };

  const handleConfirm = async () => {
    setIsUploading(true);
    try {
      const values = formik.values;

      // Serialize Slate JSON → HTML
      let htmlDescription = '';
      try {
        const nodes = JSON.parse(values.description) as Descendant[];
        htmlDescription = serializeSlateToHtml(nodes);
      } catch {
        htmlDescription = values.description;
      }

      const payload: CreateEventRequest = {
        businessId: values.businessId,
        eventName: values.eventName,
        category: values.category,
        startDateTime: new Date(values.startDateTime).toISOString(),
        eventMode: values.eventMode,
        description: htmlDescription,
        eventVisibility: values.eventVisibility,
        certificateProvided: values.certificateProvided,
        ticketType: values.ticketType,
      };

      if (values.endDateTime) payload.endDateTime = new Date(values.endDateTime).toISOString();
      if (values.registrationDeadline) payload.registrationDeadline = new Date(values.registrationDeadline).toISOString();
      if (values.eventMode === 'online') {
        payload.eventLink = values.eventLink;
      }
      if(values.eventMode === 'in_person'){
        payload.venueName = values.venueName;
        payload.fullAddress = {
          addressLine1: values.addressLine1,
          addressLine2: values.addressLine2,
          city: values.city,
          state: values.state,
          pincode: values.pincode,
          googleMapsLink: values.googleMapsLink,
        };
      }
      if (values.ticketType === 'paid' && values.price) payload.price = values.price;
      if (values.totalSeats) payload.totalSeats = values.totalSeats;

      if (bannerFile) {
        const res = await uploadService.uploadSingle(bannerFile, { folder: 'events/banners' });
        payload.eventBanner = {
          mediaFileId: res.file.id,
          url: res.file.url,
          fileName: res.file.fileName,
          type: 'image',
          contentType: bannerFile.type,
          size: bannerFile.size,
        };
      } else if (!bannerFile && event?.eventBanner) {
        payload.eventBanner = event.eventBanner;
      }

      onSubmit(payload);
    } catch (err: any) {
      toast.error(err.message || 'Failed to upload banner');
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    if (bannerPreviewUrl.startsWith('blob:')) URL.revokeObjectURL(bannerPreviewUrl);
    setBannerFile(null);
    setBannerPreviewUrl('');
    setIsPreviewMode(false);
    formik.resetForm();
    onClose();
  };

  // Preview description as HTML
  const previewHtml = (() => {
    try {
      const nodes = JSON.parse(formik.values.description) as Descendant[];
      return serializeSlateToHtml(nodes);
    } catch {
      return formik.values.description;
    }
  })();

  const isBusy = isLoading || isUploading;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">
            {isPreviewMode ? 'Preview Event' : isEdit ? 'Edit Event' : 'Create Event'}
          </Typography>
          <IconButton onClick={handleClose} size="small" disabled={isBusy}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {isPreviewMode ? (
          /* ── Preview Mode ── */
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Review the details below before submitting.
            </Typography>
            <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}>
              {bannerPreviewUrl && (
                <Box component="img" src={bannerPreviewUrl} alt="Event Banner"
                  sx={{ width: '100%', maxHeight: 280, objectFit: 'cover', borderRadius: '8px 8px 0 0', display: 'block' }}
                />
              )}
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                  <Chip label={formik.values.category} size="small" color="primary" variant="outlined" />
                  <Chip label={formik.values.eventMode === 'online' ? 'Online' : 'In Person'} size="small" />
                  <Chip label={formik.values.eventVisibility} size="small" variant="outlined" />
                  {formik.values.certificateProvided && <Chip label="Certificate" size="small" color="success" variant="outlined" />}
                </Box>

                <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>{formik.values.eventName}</Typography>

                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="caption" color="text.secondary">Start</Typography>
                    <Typography variant="body2" fontWeight={500}>{formatDateTime(formik.values.startDateTime)}</Typography>
                  </Grid>
                  {formik.values.endDateTime && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="caption" color="text.secondary">End</Typography>
                      <Typography variant="body2" fontWeight={500}>{formatDateTime(formik.values.endDateTime)}</Typography>
                    </Grid>
                  )}
                  {formik.values.registrationDeadline && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="caption" color="text.secondary">Registration Deadline</Typography>
                      <Typography variant="body2" fontWeight={500}>{formatDateTime(formik.values.registrationDeadline)}</Typography>
                    </Grid>
                  )}
                  {formik.values.totalSeats && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="caption" color="text.secondary">Total Seats</Typography>
                      <Typography variant="body2" fontWeight={500}>{formik.values.totalSeats}</Typography>
                    </Grid>
                  )}
                </Grid>

                {formik.values.eventMode === 'online' && formik.values.eventLink && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
                    <LinkIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="body2" color="primary" noWrap>{formik.values.eventLink}</Typography>
                  </Box>
                )}
                {formik.values.eventMode === 'in_person' && formik.values.venueName && (
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'flex-start', 
                    gap: 1.5, 
                    mb: 2, 
                    p: 2, 
                    bgcolor: 'grey.50', 
                    borderRadius: 2 
                  }}>
                    <LocationOn sx={{ fontSize: 20, color: 'text.secondary', mt: 0.2 }} />
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="body2" fontWeight={600} sx={{ wordBreak: 'break-word', display: 'block' }}>
                        {formik.values.venueName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-word', display: 'block', mt: 0.5, mb: formik.values.googleMapsLink ? 1.5 : 0 }}>
                        {[
                          formik.values.addressLine1,
                          formik.values.addressLine2,
                          formik.values.city,
                          formik.values.state,
                          formik.values.pincode
                        ].filter(Boolean).join(', ')}
                      </Typography>
                      
                      {formik.values.googleMapsLink && (
                        <Button
                          variant="contained"
                          size="small"
                          href={formik.values.googleMapsLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ 
                            textTransform: 'none', 
                            borderRadius: 2,
                            whiteSpace: 'nowrap',
                            height: 32,
                            px: 2
                          }}
                        >
                          Get Direction
                        </Button>
                      )}
                    </Box>
                  </Box>
                )}

                <Divider sx={{ my: 2 }} />

                {/* Description rendered as HTML */}
                <Box
                  sx={{
                    mb: 2,
                    '& p': { mb: 0.5 },
                    '& h1,& h2,& h3,& h4,& h5,& h6': { fontWeight: 600, mb: 1, mt: 1 },
                    '& ul,& ol': { pl: 3, mb: 1 },
                    '& blockquote': { borderLeft: '3px solid #ddd', pl: 1.5, color: 'text.secondary', fontStyle: 'italic', my: 1 },
                    '& code': { bgcolor: 'grey.100', px: 0.5, borderRadius: 0.5, fontFamily: 'monospace', fontSize: '0.9em' },
                    '& a': { color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } },
                  }}
                  dangerouslySetInnerHTML={{ __html: previewHtml }}
                />

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip
                    label={formik.values.ticketType === 'free' ? 'Free Entry' : `Paid — ₹${formik.values.price}`}
                    color={formik.values.ticketType === 'free' ? 'success' : 'warning'}
                    size="small"
                  />
                </Box>
              </CardContent>
            </Card>
          </Box>
        ) : (
          /* ── Form Mode ── */
          <Grid container spacing={2} sx={{ pt: 1 }}>
            {/* Business Selection */}
            <Grid size={{ xs: 12 }}>
              <Autocomplete
                fullWidth
                disabled={!!event || !!defaultBusiness}
                options={businesses}
                loading={businessesLoading}
                value={selectedBusiness}
                onChange={(_, newValue) => {
                  setSelectedBusiness(newValue);
                  formik.setFieldValue('businessId', newValue?._id || '');
                }}
                onBlur={() => formik.setFieldTouched('businessId', true)}
                getOptionLabel={(option) => option.businessName}
                isOptionEqualToValue={(option, value) => option._id === value._id}
                slotProps={{
                  listbox: {
                    onScroll: handleBusinessScroll,
                  },
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Business *"
                    size="small"
                    error={formik.touched.businessId && Boolean(formik.errors.businessId)}
                    helperText={
                      formik.touched.businessId && formik.errors.businessId
                        ? String(formik.errors.businessId)
                        : 'Select which business is organizing this event'
                    }
                    slotProps={{
                      input: {
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {businessesLoading ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      },
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <ListItem {...props} key={option._id}>
                    <ListItemAvatar>
                      <Avatar
                        src={getFileUrl(option.logoId)}
                        sx={{ width: 40, height: 40 }}
                        variant="rounded"
                      >
                        <BusinessIcon />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={option.businessName}
                      secondary={option.businessCategory}
                      slotProps={{
                        primary: { fontWeight: 500 },
                      }}
                    />
                  </ListItem>
                )}
                noOptionsText={businessesLoading ? 'Loading...' : 'No verified businesses found'}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField fullWidth label="Event Name *" name="eventName" value={formik.values.eventName}
                onChange={formik.handleChange} onBlur={formik.handleBlur} size="small"
                disabled={!!event}
                error={formik.touched.eventName && Boolean(formik.errors.eventName)}
                helperText={formik.touched.eventName && formik.errors.eventName} />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth select label="Category *" name="category" value={formik.values.category}
                onChange={formik.handleChange} onBlur={formik.handleBlur} size="small"
                disabled={!!event}
                error={formik.touched.category && Boolean(formik.errors.category)}
                helperText={formik.touched.category && formik.errors.category}>
                {EVENT_CATEGORIES.map((cat) => <MenuItem key={cat} value={cat}>{cat}</MenuItem>)}
              </TextField>
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth label="Total Seats" name="totalSeats" type="number"
                value={formik.values.totalSeats ?? ''} onChange={formik.handleChange} onBlur={formik.handleBlur}
                size="small" slotProps={{ htmlInput: { min: 1 } }}
                disabled={!!event}
                error={formik.touched.totalSeats && Boolean(formik.errors.totalSeats)}
                helperText={formik.touched.totalSeats && formik.errors.totalSeats} />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth label="Start Date & Time *" name="startDateTime" type="datetime-local"
                value={formik.values.startDateTime} onChange={formik.handleChange} onBlur={formik.handleBlur}
                size="small" slotProps={{ inputLabel: { shrink: true } }}
                error={formik.touched.startDateTime && Boolean(formik.errors.startDateTime)}
                helperText={formik.touched.startDateTime && formik.errors.startDateTime} />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth label="End Date & Time" name="endDateTime" type="datetime-local"
                value={formik.values.endDateTime} onChange={formik.handleChange} onBlur={formik.handleBlur}
                size="small" slotProps={{ inputLabel: { shrink: true } }}
                error={formik.touched.endDateTime && Boolean(formik.errors.endDateTime)}
                helperText={formik.touched.endDateTime && formik.errors.endDateTime} />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth label="Registration Deadline" name="registrationDeadline" type="datetime-local"
                value={formik.values.registrationDeadline} onChange={formik.handleChange} onBlur={formik.handleBlur}
                size="small" slotProps={{ inputLabel: { shrink: true } }}
                error={formik.touched.registrationDeadline && Boolean(formik.errors.registrationDeadline)}
                helperText={formik.touched.registrationDeadline && formik.errors.registrationDeadline} />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }} />

            <Grid size={{ xs: 12 }}><Divider /></Grid>

            {/* Event Mode */}
            <Grid size={{ xs: 12 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Event Mode *</Typography>
              <ToggleButtonGroup exclusive value={formik.values.eventMode} size="small"
                onChange={(_, value) => { if (value) formik.setFieldValue('eventMode', value); }}>
                <ToggleButton value="online">Online</ToggleButton>
                <ToggleButton value="in_person">In Person</ToggleButton>
              </ToggleButtonGroup>
            </Grid>

            {formik.values.eventMode === 'online' && (
              <Grid size={{ xs: 12 }}>
                <TextField fullWidth label="Meeting Link *" name="eventLink" value={formik.values.eventLink}
                  onChange={formik.handleChange} onBlur={formik.handleBlur} size="small" placeholder="https://..."
                  error={formik.touched.eventLink && Boolean(formik.errors.eventLink)}
                  helperText={formik.touched.eventLink && formik.errors.eventLink} />
              </Grid>
            )}

            {formik.values.eventMode === 'in_person' && (
              <>
                <Grid size={{ xs: 12 }}>
                  <TextField fullWidth label="Venue Name *" name="venueName" value={formik.values.venueName}
                    onChange={formik.handleChange} onBlur={formik.handleBlur} size="small"
                    error={formik.touched.venueName && Boolean(formik.errors.venueName)}
                    helperText={formik.touched.venueName && formik.errors.venueName} />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField fullWidth label="Address Line 1 *" name="addressLine1" value={formik.values.addressLine1}
                    onChange={formik.handleChange} onBlur={formik.handleBlur} size="small"
                    error={formik.touched.addressLine1 && Boolean(formik.errors.addressLine1)}
                    helperText={formik.touched.addressLine1 && formik.errors.addressLine1} />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField fullWidth label="Address Line 2" name="addressLine2" value={formik.values.addressLine2}
                    onChange={formik.handleChange} onBlur={formik.handleBlur} size="small"
                    error={formik.touched.addressLine2 && Boolean(formik.errors.addressLine2)}
                    helperText={formik.touched.addressLine2 && formik.errors.addressLine2} />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField fullWidth label="City *" name="city" value={formik.values.city}
                    onChange={formik.handleChange} onBlur={formik.handleBlur} size="small"
                    error={formik.touched.city && Boolean(formik.errors.city)}
                    helperText={formik.touched.city && formik.errors.city} />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField fullWidth label="State *" name="state" value={formik.values.state}
                    onChange={formik.handleChange} onBlur={formik.handleBlur} size="small"
                    error={formik.touched.state && Boolean(formik.errors.state)}
                    helperText={formik.touched.state && formik.errors.state} />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField fullWidth label="Pincode *" name="pincode" value={formik.values.pincode}
                    onChange={formik.handleChange} onBlur={formik.handleBlur} size="small"
                    error={formik.touched.pincode && Boolean(formik.errors.pincode)}
                    helperText={formik.touched.pincode && formik.errors.pincode} />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField fullWidth label="Google Maps Link (optional)" name="googleMapsLink" value={formik.values.googleMapsLink}
                    onChange={formik.handleChange} onBlur={formik.handleBlur} size="small" placeholder="https://www.google.com/maps/..."
                    error={formik.touched.googleMapsLink && Boolean(formik.errors.googleMapsLink)}
                    helperText={formik.touched.googleMapsLink && formik.errors.googleMapsLink} />
                </Grid>
              </>
            )}

            <Grid size={{ xs: 12 }}><Divider /></Grid>

            {/* Description — Rich Text */}
            <Grid size={{ xs: 12 }}>
              {event ? (
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Description
                  </Typography>
                  <Box
                    sx={{
                      p: 2,
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 1,
                      bgcolor: theme.palette.action.disabledBackground,
                      minHeight: 100,
                      '& p': { margin: 0 },
                      '& h1, & h2, & h3, & h4, & h5, & h6': { margin: '8px 0' },
                      '& ul, & ol': { margin: '8px 0', paddingLeft: '20px' },
                    }}
                    dangerouslySetInnerHTML={{ __html: event.description || 'No description' }}
                  />
                </Box>
              ) : (
                <SlateRichTextEditor
                  key={editorKey}
                  value={formik.values.description}
                  onChange={handleDescriptionChange}
                  onBlur={handleDescriptionBlur}
                  error={formik.touched.description && Boolean(formik.errors.description)}
                  helperText={
                    formik.touched.description && formik.errors.description
                      ? String(formik.errors.description)
                      : undefined
                  }
                  placeholder="Describe your event..."
                  maxLength={250}
                  title="Description"
                />
              )}
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth select label="Ticket Type *" name="ticketType" value={formik.values.ticketType}
                onChange={formik.handleChange} onBlur={formik.handleBlur} size="small"
                disabled={!!event}
                error={formik.touched.ticketType && Boolean(formik.errors.ticketType)}
                helperText={formik.touched.ticketType && formik.errors.ticketType}>
                <MenuItem value="free">Free</MenuItem>
                <MenuItem value="paid">Paid</MenuItem>
              </TextField>
            </Grid>

            {formik.values.ticketType === 'paid' && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField fullWidth label="Price *" name="price" type="number"
                  value={formik.values.price ?? ''} onChange={formik.handleChange} onBlur={formik.handleBlur}
                  size="small" slotProps={{ htmlInput: { min: 1 } }}
                  disabled={!!event}
                  error={formik.touched.price && Boolean(formik.errors.price)}
                  helperText={formik.touched.price && formik.errors.price} />
              </Grid>
            )}

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth select label="Visibility" name="eventVisibility"
                value={formik.values.eventVisibility} onChange={formik.handleChange} size="small"
                disabled={!!event}>
                <MenuItem value="public">Public</MenuItem>
                <MenuItem value="private">Private</MenuItem>
              </TextField>
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }} sx={{ display: 'flex', alignItems: 'center' }}>
              <FormControlLabel
                control={<Checkbox name="certificateProvided" checked={formik.values.certificateProvided} onChange={formik.handleChange} disabled={!!event} />}
                label="Certificate Provided"
              />
            </Grid>

            <Grid size={{ xs: 12 }}><Divider /></Grid>

            {/* Banner Image */}
            {!event && (
              <Grid size={{ xs: 12 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Event Banner (optional)</Typography>
                {bannerPreviewUrl ? (
                  <Box sx={{ position: 'relative', display: 'inline-block', width: '100%' }}>
                    <Box component="img" src={bannerPreviewUrl} alt="Banner preview"
                      sx={{ width: '100%', maxHeight: 200, objectFit: 'cover', borderRadius: 2, border: `1px solid ${theme.palette.divider}`, display: 'block' }}
                    />
                    <IconButton size="small" onClick={handleRemoveBanner}
                      sx={{ position: 'absolute', top: 6, right: 6, bgcolor: 'rgba(0,0,0,0.55)', color: '#fff', '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' } }}>
                      <Close fontSize="small" />
                    </IconButton>
                  </Box>
                ) : (
                  <Box>
                    <Button component="label" variant="outlined" startIcon={<Photo />} size="small" sx={{ textTransform: 'none' }}>
                      Upload Banner Image
                      <input type="file" hidden accept="image/*" onChange={handleBannerChange} />
                  </Button>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    Ideal dimension: 1200x600 pixels (2:1 ratio). Max size 10MB.
                  </Typography>
                </Box>
              )}
              </Grid>
            )}
          </Grid>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1.5, gap: 1 }}>
        {isPreviewMode ? (
          <>
            <Button onClick={() => setIsPreviewMode(false)} disabled={isBusy} variant="outlined" color="secondary">
              Back to Edit
            </Button>
            <Button variant="contained" onClick={handleConfirm} disabled={isBusy}
              startIcon={isBusy ? <CircularProgress size={16} /> : undefined}>
              {isBusy ? 'Submitting...' : isEdit ? 'Confirm Update' : 'Confirm & Create'}
            </Button>
          </>
        ) : (
          <>
            <Button onClick={handleClose} disabled={isBusy}>Cancel</Button>
            <Button variant="contained" onClick={() => formik.handleSubmit()} disabled={isBusy}>
              Preview
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};
